import * as SelectPrimitive from '@radix-ui/react-select'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'

import { cn } from '@maxus/website/utils'

import { fieldTriggerClassName } from '../field-trigger'

const triggerClassName =
  'flex h-8 min-w-0 items-center gap-1.5 overflow-hidden rounded-md border border-transparent bg-transparent px-2.5 text-control text-text-secondary outline-none transition-colors hover:text-text-primary focus-visible:border-brand-primary focus-visible:bg-surface-elevated focus-visible:ring-1 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-surface-active data-[state=open]:bg-surface-panel'
const contentClassName =
  'z-50 overflow-x-hidden overflow-y-auto rounded-lg border border-border-subtle bg-surface-card p-1 text-sm text-text-secondary shadow-popover'
const itemClassName =
  'relative flex h-9 cursor-default select-none items-center rounded-md px-3 pr-9 outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:text-text-disabled data-[highlighted]:bg-surface-active data-[highlighted]:text-text-primary data-[state=checked]:bg-surface-active data-[state=checked]:text-text-primary'

export type SelectOption<T extends string = string> = {
  disabled?: boolean
  label: ReactNode
  triggerLabel?: ReactNode
  value: T
}

export type SelectProps<T extends string> = {
  ariaLabel: string
  bordered?: boolean
  /** 合并到弹出层内容的额外 class（例如 `min-w-[136px]`）。 */
  contentClassName?: string
  /** 触发滚动前的弹出层最大高度，默认为 `320`。 */
  contentMaxHeight?: number | string
  collisionPadding?: SelectPrimitive.SelectContentProps['collisionPadding']
  disabled?: boolean
  onOpenChange?: SelectPrimitive.SelectProps['onOpenChange']
  onValueChange: (value: T) => void
  options: SelectOption<T>[]
  placeholder?: string
  /** 弹出层相对于触发器的对齐方式，默认为 Radix 的 `"start"`。 */
  align?: 'center' | 'end' | 'start'
  sideOffset?: number
  /** 合并到触发器的额外 class（例如 `w-[136px]`）。 */
  triggerClassName?: string
  value: T | undefined
}

export const Select = <T extends string>({
  ariaLabel,
  bordered = false,
  contentClassName: contentClassNameProp,
  contentMaxHeight = 320,
  collisionPadding = 8,
  disabled = false,
  onOpenChange,
  onValueChange,
  options,
  placeholder,
  align,
  sideOffset = 4,
  triggerClassName: triggerClassNameProp,
  value,
}: SelectProps<T>) => {
  const selectedOption = options.find(option => option.value === value)
  const contentMaxHeightValue = typeof contentMaxHeight === 'number' ? `${contentMaxHeight}px` : contentMaxHeight
  const contentStyle = {
    maxHeight: `min(${contentMaxHeightValue}, var(--radix-select-content-available-height))`,
  } satisfies CSSProperties

  return (
    <SelectPrimitive.Root
      disabled={disabled}
      onOpenChange={onOpenChange}
      onValueChange={next => onValueChange(next as T)}
      value={value}
    >
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(triggerClassName, bordered && fieldTriggerClassName, triggerClassNameProp)}
      >
        <span className="min-w-0 flex-1 truncate text-left">
          <SelectPrimitive.Value placeholder={placeholder}>
            {selectedOption?.triggerLabel ?? selectedOption?.label}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          align={align}
          avoidCollisions
          className={cn(contentClassName, contentClassNameProp)}
          collisionPadding={collisionPadding}
          position="popper"
          sideOffset={sideOffset}
          style={contentStyle}
        >
          <SelectPrimitive.Viewport>
            {options.map(option => (
              <SelectPrimitive.Item
                className={itemClassName}
                disabled={option.disabled}
                key={option.value}
                value={option.value}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-3 flex size-4 items-center justify-center text-text-primary">
                  <CheckIcon className="size-4" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
